import { request } from "../client";

import type { VoicesResponse } from "../types/features";
import type {
  SpeechSynthesisRequest,
  SpeechSynthesisResponse,
  VoiceRecommendationResponse,
  SaveVoicePreferenceRequest,
  SaveVoicePreferenceResponse,
  TextVoicesResponse,
  LanguagesListResponse,
  AudioPreloadRequest,
  AudioPreloadResponse,
} from "../types/speech";

export function createSpeechEndpoints(baseUrl: string): {
  synthesize: (
    payload: SpeechSynthesisRequest,
    token?: string | null,
  ) => Promise<SpeechSynthesisResponse>;
  getVoiceRecommendation: (
    textId: string,
    token?: string | null,
  ) => Promise<VoiceRecommendationResponse>;
  getVoices: (token?: string | null) => Promise<VoicesResponse>;
  getLanguages: (token?: string | null) => Promise<LanguagesListResponse>;
  saveVoicePreference: (
    payload: SaveVoicePreferenceRequest,
    token?: string | null,
  ) => Promise<SaveVoicePreferenceResponse>;
  getTextVoices: (
    textId: string,
    token?: string | null,
  ) => Promise<TextVoicesResponse>;
  preloadAudio: (
    payload: AudioPreloadRequest,
    token?: string | null,
  ) => Promise<AudioPreloadResponse>;
} {
  return {
    /**
     * Синтезировать речь для текста
     */
    synthesize(
      payload: SpeechSynthesisRequest,
      token?: string | null,
    ): Promise<SpeechSynthesisResponse> {
      return request<SpeechSynthesisResponse>(baseUrl, "/speech/synthesize", {
        method: "POST",
        body: payload,
        token,
      });
    },

    /**
     * Получить рекомендацию голоса для текста
     */
    getVoiceRecommendation(
      textId: string,
      token?: string | null,
    ): Promise<VoiceRecommendationResponse> {
      return request<VoiceRecommendationResponse>(
        baseUrl,
        `/speech/recommend-voice/${textId}`,
        {
          method: "GET",
          token,
        },
      );
    },

    /**
     * Получить все доступные голоса
     */
    getVoices(token?: string | null): Promise<VoicesResponse> {
      return request<VoicesResponse>(baseUrl, "/speech/voices", {
        method: "GET",
        token,
      });
    },

    /**
     * Получить доступные языки
     */
    getLanguages(token?: string | null): Promise<LanguagesListResponse> {
      return request<LanguagesListResponse>(baseUrl, "/speech/languages", {
        method: "GET",
        token,
      });
    },

    /**
     * Сохранить предпочтения голоса для текста
     */
    saveVoicePreference(
      payload: SaveVoicePreferenceRequest,
      token?: string | null,
    ): Promise<SaveVoicePreferenceResponse> {
      return request<SaveVoicePreferenceResponse>(
        baseUrl,
        "/speech/save-voice-preference",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },

    /**
     * Получить голоса для конкретного текста
     */
    getTextVoices(
      textId: string,
      token?: string | null,
    ): Promise<TextVoicesResponse> {
      return request<TextVoicesResponse>(
        baseUrl,
        `/speech/text-voices/${textId}`,
        {
          method: "GET",
          token,
        },
      );
    },

    /**
     * Предзагрузить аудио для текста
     */
    preloadAudio(
      payload: AudioPreloadRequest,
      token?: string | null,
    ): Promise<AudioPreloadResponse> {
      return request<AudioPreloadResponse>(baseUrl, "/speech/preload-audio", {
        method: "POST",
        body: payload,
        token,
      });
    },
  };
}
